// client/src/pages/admin/AdminFCMPanel.jsx

import React, { useState, useEffect } from 'react';
import useMessage from '../../hooks/useMessage';
import { ADMIN_LOGIN_ID, ADMIN_NAME } from '../../constants/roles';
import showNotification from '../../utilities/showNotification';

import './adminDashboard.css';

const AdminFCMPanel = () => {
  const {
    getRegisteredTokensInfo,
    validateAllTokens,
    syncTokensFromDB,
    sendMulticast,
    registeredTokensInfo,
  } = useMessage();

  const [fcmValidating, setFcmValidating]       = useState(false);
  const [fcmSyncing, setFcmSyncing]             = useState(false);
  const [fcmBroadcasting, setFcmBroadcasting]   = useState(false);
  const [fcmRefreshing, setFcmRefreshing]       = useState(false);
  const [fcmResult, setFcmResult]               = useState(null);
  const [fcmInfoExpanded, setFcmInfoExpanded]   = useState(false);
  const [showBroadcastModal, setShowBroadcastModal] = useState(false);
  const [broadcastTitle, setBroadcastTitle]     = useState('');
  const [broadcastBody, setBroadcastBody]       = useState('');

  useEffect(() => {
    loadFcmTokensInfo();
  }, []);

  // ── LOAD ──────────────────────────────────────────────────────
  const loadFcmTokensInfo = async () => {
    setFcmRefreshing(true);
    try {
      await getRegisteredTokensInfo(ADMIN_LOGIN_ID);
    } catch (error) {
      console.error('Failed to load tokens info:', error);
    } finally {
      setFcmRefreshing(false);
    }
  };

  // ── SYNC ──────────────────────────────────────────────────────
  const handleSyncTokens = async () => {
    setFcmSyncing(true);
    setFcmResult(null);
    try {
      const response = await syncTokensFromDB(ADMIN_LOGIN_ID);
      if (response.success) {
        setFcmResult({
          type: 'success',
          title: '🔄 Tokens Synchronized',
          message: `Successfully loaded ${response.tokensLoaded} tokens from database into memory.`,
        });
        await loadFcmTokensInfo();
      } else {
        setFcmResult({
          type: 'error',
          title: '❌ Sync Failed',
          message: response.error || 'Sync failed. Please try again.',
        });
      }
    } catch (error) {
      setFcmResult({ type: 'error', title: '❌ Sync Failed', message: error.message });
    } finally {
      setFcmSyncing(false);
    }
  };

  // ── VALIDATE ──────────────────────────────────────────────────
  const handleValidateTokens = async () => {
    setFcmValidating(true);
    setFcmResult(null);
    try {
      const response = await validateAllTokens(ADMIN_LOGIN_ID);
      if (response.success) {
        const { results } = response;
        setFcmResult({
          type: results.invalid === 0 ? 'success' : 'warning',
          title: '🔍 Token Validation Complete',
          message:
            `Total Checked: ${results.total}   ✅ Valid: ${results.valid}   ❌ Invalid: ${results.invalid}` +
            (results.invalid > 0
              ? `\n\n⚠️ ${results.invalid} user(s) need to log in again to receive notifications.`
              : '\n\n✅ All tokens are valid!'),
          details: results,
        });
        await loadFcmTokensInfo();
      } else {
        setFcmResult({
          type: 'error',
          title: '❌ Validation Failed',
          message: response.error || 'Validation failed. Please try again.',
        });
      }
    } catch (error) {
      setFcmResult({ type: 'error', title: '❌ Validation Failed', message: error.message });
    } finally {
      setFcmValidating(false);
    }
  };

  // ── BROADCAST ─────────────────────────────────────────────────
  const handleBroadcast = async () => {
    if (!broadcastTitle.trim() || !broadcastBody.trim()) {
      showNotification('Please enter both a title and a message.', 'warning');
      return;
    }
    setFcmBroadcasting(true);
    setShowBroadcastModal(false);
    try {
      const response = await sendMulticast(ADMIN_LOGIN_ID, ADMIN_NAME, broadcastTitle, broadcastBody);
      if (response.success) {
        showNotification(`Broadcast sent to ${response.totalSentMessages} users.`, 'success');
      } else {
        showNotification(`Broadcast failed: ${response.error || 'Unknown error'}`, 'error');
      }
      setBroadcastTitle('');
      setBroadcastBody('');
    } catch (error) {
      showNotification(`Broadcast failed: ${error.message}`, 'error');
    } finally {
      setFcmBroadcasting(false);
    }
  };

  const closeBroadcastModal = () => {
    setShowBroadcastModal(false);
    setBroadcastTitle('');
    setBroadcastBody('');
  };

  // ── RENDER ────────────────────────────────────────────────────
  return (
    <>
      <div className="fcm-scrollable">

        {/* Title bar */}
        <div className="fcm-title-bar">
          <h2>🔔 FCM Token Management</h2>
          <button
            onClick={loadFcmTokensInfo}
            disabled={fcmRefreshing}
            className="btn-refresh"
          >
            {fcmRefreshing ? '⟳ Refreshing...' : '⟳ Refresh'}
          </button>
        </div>

        {/* Token Stats */}
        {registeredTokensInfo && (
          <div className="tokens-stats">
            <div className="adminFCM-stat-card">
              <div className="stat-value">{registeredTokensInfo.totalRegistered ?? 0}</div>
              <div className="stat-label">Registered Tokens</div>
            </div>
            <div className="adminFCM-stat-card">
              <div className="stat-value">
                {registeredTokensInfo.users?.filter(u => u.hasToken).length ?? 0}
              </div>
              <div className="stat-label">Active Tokens</div>
            </div>
          </div>
        )}

        {/* Action Buttons */}
        <div className="admin-action-buttons">
          <button
            onClick={handleSyncTokens}
            disabled={fcmSyncing}
            className="btn-warning"
          >
            {fcmSyncing ? '🔄 Syncing...' : '🔄 Sync Tokens from DB'}
          </button>
          <button
            onClick={handleValidateTokens}
            disabled={fcmValidating || !registeredTokensInfo?.totalRegistered}
            className="btn-primary"
          >
            {fcmValidating ? '🔍 Validating...' : '🔍 Validate All Tokens'}
          </button>
          <button
            onClick={() => setShowBroadcastModal(true)}
            disabled={fcmBroadcasting || !registeredTokensInfo?.totalRegistered}
            className="btn-secondary"
          >
            {fcmBroadcasting ? '📨 Sending...' : '📨 Broadcast Notification'}
          </button>
        </div>

        {/* Result Box */}
        {fcmResult && (
          <div className={`result-box ${fcmResult.type}`}>
            <h3>{fcmResult.title}</h3>
            <pre>{fcmResult.message}</pre>

            {fcmResult.details?.invalidatedUsers?.length > 0 && (
              <details className="details-section">
                <summary>
                  👥 View Users with Invalid Tokens ({fcmResult.details.invalidatedUsers.length})
                </summary>
                <table className="users-table">
                  <thead>
                    <tr><th>Name</th><th>Email</th><th>User ID</th><th>Error</th></tr>
                  </thead>
                  <tbody>
                    {fcmResult.details.invalidatedUsers.map((user, idx) => (
                      <tr key={idx}>
                        <td data-label="Name">{user.name}</td>
                        <td data-label="Email">{user.email}</td>
                        <td data-label="User ID">{user.userId}</td>
                        <td data-label="Error" className="error-code">{user.error}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </details>
            )}

            {fcmResult.details?.failedUsers?.length > 0 && (
              <details className="details-section">
                <summary>
                  ⚠️ View Failed Deliveries ({fcmResult.details.failedUsers.length})
                </summary>
                <table className="users-table">
                  <thead>
                    <tr><th>Name</th><th>Email</th><th>User ID</th><th>Error</th></tr>
                  </thead>
                  <tbody>
                    {fcmResult.details.failedUsers.map((user, idx) => (
                      <tr key={idx}>
                        <td data-label="Name">{user.name}</td>
                        <td data-label="Email">{user.email}</td>
                        <td data-label="User ID">{user.userId}</td>
                        <td data-label="Error" className="error-code">{user.error}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </details>
            )}
          </div>
        )}

        {/* How to Use */}
        <div className="info-panel" onClick={() => setFcmInfoExpanded(prev => !prev)}>
          <h3>{fcmInfoExpanded ? '▼' : '▶'} ℹ️ How to Use</h3>
          {fcmInfoExpanded && (
            <ul>
              <li><strong>Sync Tokens from DB:</strong> Loads all FCM tokens from MongoDB into server memory. Run this after every server restart.</li>
              <li><strong>Validate All Tokens:</strong> Checks all registered tokens with Firebase. Invalid tokens are automatically removed.</li>
              <li><strong>Broadcast Notification:</strong> Sends a push notification to all registered students and instructors.</li>
              <li><strong>After a server restart:</strong> Always Sync first, then Validate, before broadcasting.</li>
              <li><strong>Invalid tokens:</strong> Affected users must log in again to regenerate their FCM token.</li>
            </ul>
          )}
        </div>

        {/* Registered Users Table */}
        {registeredTokensInfo?.users?.length > 0 && (
          <details className="registered-users-section">
            <summary>
              📋 View All Registered Users ({registeredTokensInfo.totalRegistered})
            </summary>
            <table className="users-table">
              <thead>
                <tr>
                  <th>Name</th>
                  <th>Email</th>
                  <th>User ID</th>
                  <th>Token Updated</th>
                  <th>Last Validated</th>
                </tr>
              </thead>
              <tbody>
                {registeredTokensInfo.users.map((user, idx) => (
                  <tr key={idx}>
                    <td data-label="Name">{user.name}</td>
                    <td data-label="Email" className="user-email">{user.email}</td>
                    <td data-label="User ID" className="user-id">{user.userId}</td>
                    <td data-label="Token Updated" className="date-time">
                      {user.tokenUpdatedAt ? new Date(user.tokenUpdatedAt).toLocaleString() : 'N/A'}
                    </td>
                    <td data-label="Last Validated" className="date-time">
                      {user.tokenLastValidated ? new Date(user.tokenLastValidated).toLocaleString() : 'Never'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </details>
        )}

      </div>

      {/* Broadcast Modal — outside scrollable-list to avoid z-index issues */}
      {showBroadcastModal && (
        <div className="broadcast-modal-overlay">
          <div className="broadcast-modal-content">
            <div className="broadcast-modal-header">
              <h3>📨 Broadcast Notification</h3>
              <button className="modal-close-btn" onClick={closeBroadcastModal}>✕</button>
            </div>
            <div className="broadcast-modal-body">
              <div className="broadcast-form-group">
                <label>📝 Title</label>
                <input
                  type="text"
                  value={broadcastTitle}
                  onChange={(e) => setBroadcastTitle(e.target.value)}
                  placeholder="Enter notification title..."
                  maxLength={100}
                />
              </div>
              <div className="broadcast-form-group">
                <label>💬 Message</label>
                <textarea
                  value={broadcastBody}
                  onChange={(e) => setBroadcastBody(e.target.value)}
                  placeholder="Enter notification message..."
                  rows={5}
                  maxLength={500}
                />
                <small className="char-count">{broadcastBody.length}/500 characters</small>
              </div>
              <div className="broadcast-warning">
                ⚠️ This will send a notification to ALL {registeredTokensInfo?.totalRegistered || 0} registered users!
              </div>
            </div>
            <div className="broadcast-modal-actions">
              <button className="btn-cancel" onClick={closeBroadcastModal}>Cancel</button>
              <button
                className="btn-send"
                onClick={handleBroadcast}
                disabled={!broadcastTitle.trim() || !broadcastBody.trim()}
              >
                📨 Send Broadcast
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AdminFCMPanel;
